import dice from 'fast-dice-coefficient';
import { clientFetch } from '~/lib/.client/fetch';
import type { ImportDeclaration, ModuleDeclaration } from '~/lib/static-analysis/esm';
import MagicString from '~/lib/static-analysis/magic';
import { renderESM } from '~/lib/static-analysis/render-esm';
import { workbenchStore } from '~/lib/stores/workbench';

const targets = new Set([
  'lucide',
  'lucide-react',
  'lucide-svelte',
  'lucide-solid',
  'lucide-vue-next',
  'lucide-preact',
  'lucide-react-native',
  '@lucide/lab',
]);

const iconsMap = new Map<string, Set<string>>();

export function clearIcons() {
  iconsMap.clear();
}

export async function transformLucide(code: MagicString, modules: ModuleDeclaration[]) {
  const lucideModules = modules
    .filter((decl) => decl.type === 'import' && targets.has(decl.module.path))
    .map((decl) => (decl as ImportDeclaration).module.path);

  const uniqueModules = [...new Set(lucideModules)];

  await Promise.all(
    uniqueModules.map(async (mod) => {
      if (iconsMap.has(mod)) {
        return;
      }

      const icons = await getIcons(mod);

      if (icons.length > 0) {
        iconsMap.set(mod, new Set(icons));
      }
    }),
  );

  for (const decl of modules) {
    if (decl.type === 'import' && targets.has(decl.module.path) && decl.namedImports) {
      let hasChanges = false;

      if (!iconsMap.has(decl.module.path)) {
        return;
      }

      const icons = iconsMap.get(decl.module.path)!;

      decl.namedImports.forEach((binding) => {
        if (!icons.has(binding.specifier)) {
          const nearestValidIcon = findNearestValidIcon(binding.specifier, icons);

          if (nearestValidIcon.length === 0) {
            return;
          }

          if (!binding.alias) {
            binding.alias = binding.specifier;
          }

          hasChanges = true;
          binding.specifier = nearestValidIcon;
        }
      });

      if (hasChanges) {
        code.replace(decl.startIndex, decl.endIndex, renderESM(decl));
      }
    }
  }
}

function getVersion(library: string) {
  try {
    const packageJson = workbenchStore.readFile('package.json').content;
    const parsed = JSON.parse(packageJson);
    const dependency = parsed.dependencies[library] || parsed.devDependencies[library];

    return dependency.match(/\d+\.\d+\.\d+/)[0];
  } catch {
    return 'latest';
  }
}

async function getIcons(library: string) {
  try {
    const version = getVersion(library);

    const response = await clientFetch('/api/lucide', {
      method: 'POST',
      body: JSON.stringify({
        library,
        version,
      }),
    });

    const { icons } = await response.json<{ icons: string[] }>();

    return icons;
  } catch (error) {
    console.error(error);
    return [];
  }
}

function findNearestValidIcon(name: string, icons: Set<string>): string {
  let maxValue = -1;
  let bestMatch = '';

  for (const iconName of icons) {
    const value = dice(iconName, name);

    if (value > maxValue) {
      maxValue = value;
      bestMatch = iconName;
    }
  }

  return bestMatch;
}
